/**
 * Day-8 product polish — settings popover, help overlay, progress tracking,
 * analytics hook, and a uniform error boundary.
 *
 * Consumed by both the gallery (`src/app.ts`) and every activity iframe.
 * Design principle: one import + one mount call. No framework, no globals
 * leaked except the `ef.*` localStorage keys.
 *
 *   import { progress, analytics, mountPolishTools, installErrorBoundary } from '/shared/polish.ts';
 *
 * `installErrorBoundary()` auto-runs when this module is imported so every
 * activity and the gallery get the safety net for free.
 */

/* ============================================================
   localStorage keys — single place so nothing goes out of sync
   ============================================================ */

const KEY_SOUND = 'ef-muted';                 // '1' = muted, '0' or absent = on (matches per-activity synth)
const KEY_REDUCED_MOTION = 'ef.reducedMotion';// '1' | (absent)
const KEY_PROGRESS = 'ef.progress';           // JSON array of activity ids
const KEY_HELP_SEEN = 'ef.help.seen';         // JSON map { [id]: 1 }

/* ============================================================
   Progress (which sims the user has opened)
   ============================================================ */

function readProgress(): Set<string> {
  try {
    const raw = localStorage.getItem(KEY_PROGRESS);
    if (!raw) return new Set();
    const arr = JSON.parse(raw);
    return new Set(Array.isArray(arr) ? arr : []);
  } catch {
    return new Set();
  }
}

function writeProgress(set: Set<string>): void {
  try { localStorage.setItem(KEY_PROGRESS, JSON.stringify([...set])); } catch {/* ignore */}
}

export const progress = {
  markOpened(id: string): void {
    const set = readProgress();
    if (set.has(id)) return;
    set.add(id);
    writeProgress(set);
    window.dispatchEvent(new CustomEvent('ef-progress-change', { detail: { id } }));
  },
  isOpened(id: string): boolean {
    return readProgress().has(id);
  },
  opened(): Set<string> {
    return readProgress();
  },
  reset(): void {
    try { localStorage.removeItem(KEY_PROGRESS); } catch {/* ignore */}
    try { localStorage.removeItem(KEY_HELP_SEEN); } catch {/* ignore */}
    window.dispatchEvent(new CustomEvent('ef-progress-change', { detail: { reset: true } }));
  },
};

/* ============================================================
   Analytics — privacy-friendly, drop-in placeholder
   ============================================================
   Emits a window event so a single Plausible/Umami `<script>` in
   src/index.html (added in Day 11 once a domain is live) can
   listen and forward. No PII. Opt-out persisted in localStorage.
   ============================================================ */

const KEY_ANALYTICS_OFF = 'ef.analytics.off';
const KEY_ANALYTICS_LOG = 'ef.analytics.log';
const ANALYTICS_LOG_CAP = 50;
const ANALYTICS_LOG_TTL_MS = 7 * 24 * 60 * 60 * 1000;

type AnalyticsEntry = { event: string; props: Record<string, unknown>; at: number };

function pushToRing(entry: AnalyticsEntry): void {
  try {
    const raw = localStorage.getItem(KEY_ANALYTICS_LOG);
    const prev: AnalyticsEntry[] = raw ? JSON.parse(raw) : [];
    const cutoff = entry.at - ANALYTICS_LOG_TTL_MS;
    const arr = prev.filter(e => typeof e.at === 'number' && e.at >= cutoff);
    arr.push(entry);
    while (arr.length > ANALYTICS_LOG_CAP) arr.shift();
    localStorage.setItem(KEY_ANALYTICS_LOG, JSON.stringify(arr));
  } catch {/* storage full or JSON broken — silently drop */}
}

export const analytics = {
  track(event: string, props: Record<string, unknown> = {}): void {
    if (localStorage.getItem(KEY_ANALYTICS_OFF) === '1') return;
    const entry: AnalyticsEntry = { event, props, at: Date.now() };
    try {
      pushToRing(entry);
      window.dispatchEvent(new CustomEvent('ef-analytics', { detail: entry }));
      // Forward iframe events up to parent so a provider script in the
      // gallery shell catches everything with a single listener.
      if (window.parent && window.parent !== window) {
        window.parent.postMessage({ type: 'ef-analytics', detail: entry }, '*');
      }
      // Plausible / Umami bridge — if the provider script is live, forward.
      const plausible = (window as unknown as { plausible?: (e: string, o?: unknown) => void }).plausible;
      if (typeof plausible === 'function') plausible(event, { props });
    } catch {/* ignore */}
  },
  enabled(): boolean {
    return localStorage.getItem(KEY_ANALYTICS_OFF) !== '1';
  },
  setEnabled(v: boolean): void {
    if (v) {
      try { localStorage.removeItem(KEY_ANALYTICS_OFF); } catch {/* ignore */}
    } else {
      try { localStorage.setItem(KEY_ANALYTICS_OFF, '1'); } catch {/* ignore */}
    }
  },
  /** Inspect the last-N captured events (debug). Empty when opted-out. */
  recent(): AnalyticsEntry[] {
    try {
      const raw = localStorage.getItem(KEY_ANALYTICS_LOG);
      return raw ? JSON.parse(raw) : [];
    } catch { return []; }
  },
  clear(): void {
    try { localStorage.removeItem(KEY_ANALYTICS_LOG); } catch {/* ignore */}
  },
};

/* ============================================================
   Sound — mirrors the per-activity synth.muted state
   ============================================================ */

function soundOn(): boolean {
  return localStorage.getItem(KEY_SOUND) !== '1';
}
function setSoundOn(v: boolean): void {
  try { localStorage.setItem(KEY_SOUND, v ? '0' : '1'); } catch {/* ignore */}
  // Notify the activity's synth (if present) — matches its setMuted contract.
  const synth = (window as unknown as { synth?: { setMuted(m: boolean): void } }).synth;
  try { synth?.setMuted(!v); } catch {/* ignore */}
  window.dispatchEvent(new CustomEvent('ef-sound-change', { detail: { on: v } }));
}

/* ============================================================
   Reduced-motion override (persists across sessions)
   ============================================================ */

function reducedMotionOn(): boolean {
  return localStorage.getItem(KEY_REDUCED_MOTION) === '1';
}
function setReducedMotionOn(v: boolean): void {
  try { v ? localStorage.setItem(KEY_REDUCED_MOTION, '1') : localStorage.removeItem(KEY_REDUCED_MOTION); }
  catch {/* ignore */}
  applyReducedMotion();
}
function applyReducedMotion(): void {
  // Toggle a body class the CSS watches; layered on top of the OS preference.
  const root = document.documentElement;
  root.classList.toggle('ef-force-reduced-motion', reducedMotionOn());
}

/* ============================================================
   Settings + Help mount — the two #tools buttons
   ============================================================ */

interface MountOpts {
  /** Activity id, e.g. "5.1". Omit in the gallery. */
  activity?: string;
  /** Help cards shown in the overlay. Falls back to a generic card set. */
  help?: Array<{ title: string; body: string }>;
}

export function mountPolishTools(container: HTMLElement, opts: MountOpts = {}): void {
  if (!container || container.dataset.polishMounted === '1') return;
  container.dataset.polishMounted = '1';
  applyReducedMotion();

  // Help button first (left of settings)
  const help = document.createElement('button');
  help.type = 'button';
  help.className = 'ef-tool';
  help.setAttribute('aria-label', 'Help');
  help.title = 'Help';
  help.innerHTML = '<svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="10" cy="10" r="7.5"/><path d="M7.5 7.5a2.5 2.5 0 1 1 3.8 2.15c-.7.4-1.3.9-1.3 1.85"/><circle cx="10" cy="14.25" r="0.75" fill="currentColor" stroke="none"/></svg>';
  help.addEventListener('click', () => openHelpOverlay(opts));
  container.appendChild(help);

  // Settings button
  const settings = document.createElement('button');
  settings.type = 'button';
  settings.className = 'ef-tool';
  settings.setAttribute('aria-label', 'Settings');
  settings.setAttribute('aria-haspopup', 'true');
  settings.setAttribute('aria-expanded', 'false');
  settings.title = 'Settings';
  settings.innerHTML = '<svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="10" cy="10" r="2.4"/><path d="M10 1.5v2.5M10 16v2.5M1.5 10h2.5M16 10h2.5M3.9 3.9l1.8 1.8M14.3 14.3l1.8 1.8M3.9 16.1l1.8-1.8M14.3 5.7l1.8-1.8"/></svg>';
  settings.addEventListener('click', () => toggleSettingsPopover(settings, opts));
  container.appendChild(settings);

  // Auto-open help on first visit to an activity.
  if (opts.activity && !helpSeen(opts.activity)) {
    // Delay one tick so the iframe has painted — avoids overlay-over-loading flash.
    setTimeout(() => openHelpOverlay(opts), 350);
  }
}

/* ---------- settings popover ---------- */

let currentPopover: HTMLElement | null = null;

function toggleSettingsPopover(anchor: HTMLButtonElement, opts: MountOpts): void {
  if (currentPopover) { closeSettingsPopover(); return; }

  const pop = document.createElement('div');
  pop.className = 'ef-popover';
  pop.setAttribute('role', 'dialog');
  pop.setAttribute('aria-label', 'Settings');
  pop.innerHTML = `
    <div class="ef-popover__title">Settings</div>
    <label class="ef-popover__row">
      <span>Sound</span>
      <input type="checkbox" class="ef-switch" data-setting="sound">
    </label>
    <label class="ef-popover__row">
      <span>Reduced motion</span>
      <input type="checkbox" class="ef-switch" data-setting="reducedMotion">
    </label>
    <label class="ef-popover__row">
      <span>Usage analytics</span>
      <input type="checkbox" class="ef-switch" data-setting="analytics">
    </label>
    <hr class="ef-popover__sep">
    <button type="button" class="ef-popover__btn" data-action="reset-progress">Reset progress</button>
  `;

  const qs = <T extends HTMLElement>(s: string) => pop.querySelector<T>(s)!;
  const sound = qs<HTMLInputElement>('input[data-setting="sound"]');
  const rm = qs<HTMLInputElement>('input[data-setting="reducedMotion"]');
  const an = qs<HTMLInputElement>('input[data-setting="analytics"]');
  sound.checked = soundOn();
  rm.checked = reducedMotionOn();
  an.checked = analytics.enabled();

  sound.addEventListener('change', () => { setSoundOn(sound.checked); analytics.track('settings.sound', { on: sound.checked }); });
  rm.addEventListener('change', () => { setReducedMotionOn(rm.checked); analytics.track('settings.reduced-motion', { on: rm.checked }); });
  an.addEventListener('change', () => { analytics.setEnabled(an.checked); });
  qs<HTMLButtonElement>('button[data-action="reset-progress"]').addEventListener('click', () => {
    progress.reset();
    analytics.track('settings.reset-progress');
    closeSettingsPopover();
  });

  document.body.appendChild(pop);
  positionPopover(pop, anchor);
  anchor.setAttribute('aria-expanded', 'true');
  currentPopover = pop;

  // Dismiss on outside tap / Escape
  const onPointerDown = (ev: PointerEvent) => {
    const t = ev.target as Node;
    if (!pop.contains(t) && t !== anchor && !anchor.contains(t)) closeSettingsPopover();
  };
  const onKey = (ev: KeyboardEvent) => { if (ev.key === 'Escape') closeSettingsPopover(); };
  setTimeout(() => {
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKey);
  }, 0);
  pop.dataset.cleanup = '1';
  pop.addEventListener('ef-cleanup', () => {
    document.removeEventListener('pointerdown', onPointerDown);
    document.removeEventListener('keydown', onKey);
    anchor.setAttribute('aria-expanded', 'false');
  });
  analytics.track('settings.open', { activity: opts.activity ?? 'gallery' });
}

function closeSettingsPopover(): void {
  if (!currentPopover) return;
  currentPopover.dispatchEvent(new CustomEvent('ef-cleanup'));
  currentPopover.remove();
  currentPopover = null;
}

function positionPopover(pop: HTMLElement, anchor: HTMLElement): void {
  const r = anchor.getBoundingClientRect();
  const popW = 240;
  const left = Math.max(8, Math.min(window.innerWidth - popW - 8, r.right - popW));
  const top = r.bottom + 8;
  pop.style.left = `${left}px`;
  pop.style.top = `${top}px`;
  pop.style.width = `${popW}px`;
}

/* ---------- help overlay ---------- */

function readHelpSeen(): Record<string, 1> {
  try {
    const raw = localStorage.getItem(KEY_HELP_SEEN);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch { return {}; }
}
function helpSeen(id: string): boolean {
  return !!readHelpSeen()[id];
}
function markHelpSeen(id: string): void {
  try {
    const s = readHelpSeen();
    s[id] = 1;
    localStorage.setItem(KEY_HELP_SEEN, JSON.stringify(s));
  } catch {/* ignore */}
}

function defaultHelpCards(activity?: string): Array<{ title: string; body: string }> {
  const context = activity ? 'this activity' : 'this app';
  return [
    { title: 'Tap, don’t drag', body: `${context[0].toUpperCase() + context.slice(1)} is built for touch screens. Use the steppers and buttons — no keyboard needed.` },
    { title: 'Play the demo', body: 'Press the ▶ Play button to run a canned walkthrough. Press Reset to return to the starting state at any time.' },
    { title: 'Go fullscreen', body: 'Tap the ⤢ Fullscreen button to fill the classroom screen. Tap again or press Escape to exit.' },
  ];
}

function openHelpOverlay(opts: MountOpts): void {
  const cards = (opts.help && opts.help.length > 0) ? opts.help : defaultHelpCards(opts.activity);
  const overlay = document.createElement('div');
  overlay.className = 'ef-help';
  overlay.setAttribute('role', 'dialog');
  overlay.setAttribute('aria-modal', 'true');
  overlay.setAttribute('aria-label', 'Help');
  overlay.innerHTML = `
    <div class="ef-help__backdrop" data-action="close"></div>
    <div class="ef-help__card">
      <button type="button" class="ef-help__close" aria-label="Close" data-action="close">×</button>
      <div class="ef-help__viewport">
        <div class="ef-help__track">
          ${cards.map((c) => `
            <section class="ef-help__slide">
              <h2>${escapeHtml(c.title)}</h2>
              <p>${escapeHtml(c.body).replace(/&lt;(\/?)b&gt;/g, '<$1b>')}</p>
            </section>
          `).join('')}
        </div>
      </div>
      <div class="ef-help__dots" role="tablist" aria-label="Help pages">
        ${cards.map((_, i) => `<button type="button" class="ef-help__dot" role="tab" aria-selected="${i === 0}" data-idx="${i}"></button>`).join('')}
      </div>
      <div class="ef-help__nav">
        <button type="button" class="ef-help__prev" aria-label="Previous">‹</button>
        <button type="button" class="ef-help__next" aria-label="Next">›</button>
      </div>
    </div>
  `;
  document.body.appendChild(overlay);

  const track = overlay.querySelector<HTMLElement>('.ef-help__track')!;
  const dots = [...overlay.querySelectorAll<HTMLButtonElement>('.ef-help__dot')];
  const slides = [...overlay.querySelectorAll<HTMLElement>('.ef-help__slide')];
  let idx = 0;
  const go = (i: number): void => {
    idx = Math.max(0, Math.min(cards.length - 1, i));
    track.style.transform = `translateX(${-idx * 100}%)`;
    dots.forEach((d, j) => d.setAttribute('aria-selected', String(j === idx)));
    overlay.querySelector<HTMLButtonElement>('.ef-help__prev')!.disabled = (idx === 0);
    overlay.querySelector<HTMLButtonElement>('.ef-help__next')!.disabled = (idx === cards.length - 1);
  };
  go(0);

  overlay.querySelector<HTMLButtonElement>('.ef-help__prev')!.addEventListener('click', () => go(idx - 1));
  overlay.querySelector<HTMLButtonElement>('.ef-help__next')!.addEventListener('click', () => go(idx + 1));
  dots.forEach((d) => d.addEventListener('click', () => go(Number(d.dataset.idx))));

  // Swipe
  let startX = 0, dx = 0, dragging = false;
  const viewport = overlay.querySelector<HTMLElement>('.ef-help__viewport')!;
  viewport.addEventListener('pointerdown', (ev) => {
    dragging = true; startX = ev.clientX; dx = 0;
    track.style.transition = 'none';
    viewport.setPointerCapture(ev.pointerId);
  });
  viewport.addEventListener('pointermove', (ev) => {
    if (!dragging) return;
    dx = ev.clientX - startX;
    const base = -idx * slides[0].offsetWidth;
    track.style.transform = `translateX(${base + dx}px)`;
  });
  viewport.addEventListener('pointerup', (ev) => {
    if (!dragging) return;
    dragging = false;
    try { viewport.releasePointerCapture(ev.pointerId); } catch {/* ignore */}
    track.style.transition = '';
    const threshold = slides[0].offsetWidth * 0.18;
    if (dx <= -threshold && idx < cards.length - 1) go(idx + 1);
    else if (dx >= threshold && idx > 0) go(idx - 1);
    else go(idx);
  });

  const close = (): void => {
    if (opts.activity) markHelpSeen(opts.activity);
    overlay.classList.add('ef-help--closing');
    setTimeout(() => overlay.remove(), 220);
    window.removeEventListener('keydown', onKey);
  };
  overlay.addEventListener('click', (ev) => {
    const t = ev.target as HTMLElement;
    if (t.dataset.action === 'close') close();
  });
  const onKey = (ev: KeyboardEvent): void => {
    if (ev.key === 'Escape') close();
    else if (ev.key === 'ArrowLeft') go(idx - 1);
    else if (ev.key === 'ArrowRight') go(idx + 1);
  };
  window.addEventListener('keydown', onKey);

  // Mount animation
  requestAnimationFrame(() => overlay.classList.add('ef-help--open'));
  analytics.track('help.open', { activity: opts.activity ?? 'gallery' });
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]!));
}

/* ============================================================
   Error boundary — auto-installs on import
   ============================================================
   Fires once per page. If an uncaught exception escapes the module
   graph (non-hero sims using Canvas 2D have no try/catch around
   their mount), we paint a friendly overlay inside the closest
   #stage or on top of the body. The sim is allowed to keep running
   for any ongoing rAF that's still healthy.
   ============================================================ */

let errorBoundaryInstalled = false;
let errorShown = false;

export function installErrorBoundary(): void {
  if (errorBoundaryInstalled) return;
  errorBoundaryInstalled = true;

  const render = (msg: string): void => {
    if (errorShown) return;
    errorShown = true;
    const host = document.getElementById('stage') ?? document.body;
    const card = document.createElement('div');
    card.className = 'ef-error-card';
    card.setAttribute('role', 'alert');
    card.innerHTML = `
      <div class="ef-error-card__title">Something went wrong</div>
      <p class="ef-error-card__msg">${escapeHtml(msg || 'This activity stopped unexpectedly.')}</p>
      <div class="ef-error-card__row">
        <button type="button" class="ef-error-card__btn" data-action="reload">Reload activity</button>
        <button type="button" class="ef-error-card__btn ef-error-card__btn--ghost" data-action="home">Back to gallery</button>
      </div>
    `;
    card.querySelector<HTMLButtonElement>('[data-action="reload"]')!.addEventListener('click', () => location.reload());
    card.querySelector<HTMLButtonElement>('[data-action="home"]')!.addEventListener('click', () => {
      try { (window.parent ?? window).postMessage({ type: 'ef-nav', hash: '' }, '*'); } catch {/* ignore */}
      try { if (window.parent !== window) return; } catch {/* cross-origin — ignore */}
      try { top!.location.hash = ''; } catch {/* ignore */}
    });
    host.appendChild(card);
    analytics.track('error', { msg });
  };

  window.addEventListener('error', (ev) => {
    // Ignore benign resource failures (missing icon, etc.) — only trap script errors.
    if (ev.error || ev.message) render(String(ev.message || ev.error || 'Unknown error'));
  });
  window.addEventListener('unhandledrejection', (ev) => {
    render(String(ev.reason?.message ?? ev.reason ?? 'Unknown error'));
  });
}

installErrorBoundary();

/* ============================================================
   Listen for sound changes elsewhere (e.g. another tab)
   ============================================================ */

window.addEventListener('storage', (ev) => {
  if (ev.key === KEY_SOUND) {
    const synth = (window as unknown as { synth?: { setMuted(m: boolean): void } }).synth;
    try { synth?.setMuted(!soundOn()); } catch {/* ignore */}
  } else if (ev.key === KEY_REDUCED_MOTION) {
    applyReducedMotion();
  }
});

/* ============================================================
   Draggable metrics readout — shared across activities
   ============================================================
   Every activity with a `#readout` panel over the stage gets
   pointer-driven repositioning. Uses transform so the original
   flex-layout placement is preserved (some activities anchor
   the readout top-left, others top-right). Clamps to stage
   bounds and re-clamps when the stage resizes (orientation or
   fullscreen change). In-memory only; resets on page reload.
   ============================================================ */

export function installReadoutDrag(): void {
  const readout = document.getElementById('readout') as HTMLElement | null;
  if (!readout) return;
  const stage = (document.getElementById('stage') ?? readout.parentElement) as HTMLElement | null;
  if (!stage) return;

  let tx = 0, ty = 0;
  let lastX = 0, lastY = 0;
  let activeId: number | null = null;

  const clamp = (): void => {
    const rr = readout.getBoundingClientRect();
    const sr = stage.getBoundingClientRect();
    let cx = 0, cy = 0;
    if (rr.left < sr.left) cx = sr.left - rr.left;
    else if (rr.right > sr.right) cx = sr.right - rr.right;
    if (rr.top < sr.top) cy = sr.top - rr.top;
    else if (rr.bottom > sr.bottom) cy = sr.bottom - rr.bottom;
    if (cx || cy) {
      tx += cx; ty += cy;
      readout.style.transform = `translate(${tx}px, ${ty}px)`;
    }
  };

  readout.addEventListener('pointerdown', (e) => {
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    activeId = e.pointerId;
    readout.setPointerCapture(activeId);
    lastX = e.clientX; lastY = e.clientY;
    readout.classList.add('ef-dragging');
    e.preventDefault();
  });

  readout.addEventListener('pointermove', (e) => {
    if (activeId === null || e.pointerId !== activeId) return;
    const dx = e.clientX - lastX;
    const dy = e.clientY - lastY;
    lastX = e.clientX; lastY = e.clientY;
    tx += dx; ty += dy;
    readout.style.transform = `translate(${tx}px, ${ty}px)`;
    clamp();
  });

  const endDrag = (e: PointerEvent): void => {
    if (activeId === null || e.pointerId !== activeId) return;
    readout.classList.remove('ef-dragging');
    if (readout.hasPointerCapture(activeId)) readout.releasePointerCapture(activeId);
    activeId = null;
  };
  readout.addEventListener('pointerup', endDrag);
  readout.addEventListener('pointercancel', endDrag);

  const ro = new ResizeObserver(() => { if (tx !== 0 || ty !== 0) clamp(); });
  ro.observe(stage);
}
